import React from "react";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { MONTH_NAMES, STATUS_KEYS, STATUS_META } from "../utils/constants";
import { formatMoney } from "../utils/format";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip);

const TICK_COLOR = "#a1a1aa";

/* The reference's "Work Order Analysis Dashboard": monthly bar chart + status doughnut, then `children` (the schedule widget). */
export default function AnalyticsDashboard({ dashboard, currency, year, onYearChange, metric, onMetricChange, onStatusClick, children }) {
  const { metrics, statuses, monthly, years } = dashboard;
  const isRevenue = metric === "revenue";
  const accent = isRevenue ? "#3b82f6" : "#ee3e51";

  const barData = {
    labels: MONTH_NAMES,
    datasets: [
      {
        label: isRevenue ? `Revenue (${currency})` : "Work Orders",
        data: monthly.map((m) => (isRevenue ? m.value : m.count)),
        backgroundColor: isRevenue ? "rgba(59, 130, 246, 0.7)" : "rgba(238, 62, 81, 0.7)",
        borderColor: accent,
        borderWidth: 1.5,
        borderRadius: 6,
        hoverBackgroundColor: accent,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => (isRevenue ? ` Revenue: ${formatMoney(ctx.raw, currency)}` : ` Orders: ${ctx.raw}`),
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: TICK_COLOR, font: { family: "Outfit", size: 11 } } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(255, 255, 255, 0.06)" },
        ticks: {
          color: TICK_COLOR,
          font: { family: "Outfit", size: 10 },
          precision: isRevenue ? undefined : 0,
          callback: (val) => (isRevenue && val >= 1000 ? `${val / 1000}k` : val),
        },
      },
    },
  };

  const total = metrics.total;
  const completionRate = total > 0 ? Math.round((statuses.completed.count / total) * 100) : 0;

  const doughnutData = {
    labels: STATUS_KEYS.map((key) => STATUS_META[key].chartLabel),
    datasets: [
      {
        data: STATUS_KEYS.map((key) => statuses[key].count),
        backgroundColor: STATUS_KEYS.map((key) => STATUS_META[key].color),
        borderColor: "#121215",
        borderWidth: 3,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.raw || 0;
            const pct = total > 0 ? Math.round((value / total) * 100) : 0;
            return ` ${ctx.label}: ${value} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <section className="analytics-dashboard-card">
      <div className="analytics-header-bar">
        <div className="analytics-title-group">
          <h2>📊 Work Order Analysis Dashboard</h2>
          <span className="analytics-badge">Live Performance Insights</span>
        </div>
        <div className="analytics-controls">
          <div className="filter-item">
            <label htmlFor="analytics-year-select">Filter Year:</label>
            <select id="analytics-year-select" value={year} onChange={(e) => onYearChange(e.target.value)}>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  Year {y}
                </option>
              ))}
              <option value="ALL">All Years</option>
            </select>
          </div>
          <div className="chart-metric-toggle-group">
            <button
              type="button"
              className={`btn-toggle ${!isRevenue ? "active" : ""}`}
              onClick={() => onMetricChange("count")}
            >
              Order Count
            </button>
            <button
              type="button"
              className={`btn-toggle ${isRevenue ? "active" : ""}`}
              onClick={() => onMetricChange("revenue")}
            >
              Revenue ({currency})
            </button>
          </div>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card chart-card">
          <div className="card-header-sm">
            <div className="card-header-info">
              <h3>Monthly Work Order Trend ({year === "ALL" ? "All Time" : year})</h3>
              <span className="chart-subtext">
                {isRevenue ? `Total Registered Revenue (${currency}) per Month` : "Total Work Orders Registered per Month"}
              </span>
            </div>
            <span className="chart-legend-tag">12 Months Overview</span>
          </div>
          <div className="chart-canvas-wrapper">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>

        <div className="analytics-card status-widget-card">
          <div className="card-header-sm">
            <div className="card-header-info">
              <h3>Work Order Status Breakdown</h3>
              <span className="chart-subtext">Live order lifecycle status distribution</span>
            </div>
          </div>
          <div className="status-widget-body">
            <div className="status-chart-container">
              <Doughnut data={doughnutData} options={doughnutOptions} />
              <div className="status-doughnut-center">
                <span className="center-value">{completionRate}%</span>
                <span className="center-label">Completed</span>
              </div>
            </div>

            <div className="status-legend-grid">
              {STATUS_KEYS.map((key) => (
                <div
                  key={key}
                  className={`status-legend-item ${STATUS_META[key].legendClass}`}
                  title={`Click to filter table by ${STATUS_META[key].label} status`}
                  role="button"
                  tabIndex={0}
                  onClick={() => onStatusClick(key)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onStatusClick(key)}
                >
                  <div className="legend-header">
                    <span className={`dot-indicator ${STATUS_META[key].dot}`}></span>
                    <span className="legend-name">{STATUS_META[key].chartLabel}</span>
                  </div>
                  <div className="legend-stats">
                    <span className="legend-count">{statuses[key].count}</span>
                    <span className="legend-value">{formatMoney(statuses[key].value, currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {children}
      </div>
    </section>
  );
}
